import React, { useState, useMemo, useCallback } from "react";
import {
  Search,
  RefreshCw,
  Download,
  AlertTriangle,
  TrendingDown,
  CheckCircle,
  Package,
  Settings,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  HelpCircle,
  Plus,
  Check,
} from "lucide-react";
import { useSearchParams } from "react-router-dom";
import {
  usePurchaseStockAnalysisQuery,
  GetPurchaseStockAnalysisRequest,
  StockStatusFilter,
  MaterialCategoryFilter,
  StockAnalysisSortBy,
  formatNumber,
  formatCurrency,
  StockAnalysisItemDto,
} from "../../api/hooks/usePurchaseStockAnalysis";
import { GridColumn, GridHeader, ColumnChooser, useGridLayout } from '../../features/grid-layout';
import { StockSeverity } from "../../api/generated/api-client";
import { getAuthenticatedApiClient } from "../../api/client";
import { exportToXlsx } from "../../utils/exportToXlsx";
import { useToast } from "../../contexts/ToastContext";
import CatalogDetail from "./CatalogDetail";
import { PAGE_CONTAINER_HEIGHT } from "../../constants/layout";
import { TimePeriod, resolveTimePeriod } from "../../utils/timePeriod";
import { usePurchasePlanningList } from "../../contexts/PurchasePlanningListContext";
import PurchasePlanningListPanel from "../common/PurchasePlanningListPanel";
import { useScreenView } from "../../telemetry/useScreenView";
import ProductNameSuffix from "../common/ProductNameSuffix";

const DEFAULT_MATERIAL_CATEGORY = MaterialCategoryFilter.Other;

const MATERIAL_CATEGORY_OPTIONS: ReadonlyArray<{
  value: MaterialCategoryFilter;
  label: string;
}> = [
  { value: MaterialCategoryFilter.Other, label: "Suroviny" },
  { value: MaterialCategoryFilter.Labels, label: "Etikety" },
  { value: MaterialCategoryFilter.Packaging, label: "Obaly" },
  { value: MaterialCategoryFilter.All, label: "Vše" },
];

const PurchaseStockAnalysis: React.FC = () => {
  const [searchParams] = useSearchParams();
  
  // State for filters - initialize from URL parameters
  const [filters, setFilters] = useState<GetPurchaseStockAnalysisRequest>(() => {
    const stockStatusParam = searchParams.get('StockStatus');

    // Map URL parameter to enum value
    let stockStatus = StockStatusFilter.All;
    if (stockStatusParam && Object.values(StockStatusFilter).includes(stockStatusParam as StockStatusFilter)) {
      stockStatus = stockStatusParam as StockStatusFilter;
    }

    const materialCategoryParam = searchParams.get('MaterialCategory');

    let materialCategory = DEFAULT_MATERIAL_CATEGORY;
    if (materialCategoryParam && Object.values(MaterialCategoryFilter).includes(materialCategoryParam as MaterialCategoryFilter)) {
      materialCategory = materialCategoryParam as MaterialCategoryFilter;
    }

    return {
      fromDate: new Date(
        new Date().getFullYear() - 1,
        new Date().getMonth(),
        new Date().getDate(),
      ),
      toDate: new Date(),
      stockStatus: stockStatus,
      materialCategory: materialCategory,
      onlyConfigured: true,
      searchTerm: searchParams.get('searchTerm') || "",
      pageNumber: 1,
      pageSize: 20,
      sortBy: StockAnalysisSortBy.StockEfficiency,
      sortDescending: false,
    };
  });

  // State for product detail modal
  const [selectedProductCode, setSelectedProductCode] = useState<string | null>(
    null,
  );
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  // State for collapsible sections
  const [isControlsCollapsed, setIsControlsCollapsed] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  useScreenView('Purchase', 'PurchaseStockAnalysis');

  const { showError } = useToast();

  // Query for stock analysis data
  const { data, isLoading, error, isRefetching, refetch } =
    usePurchaseStockAnalysisQuery(filters);

  // Purchase planning list functionality
  const { addItem: addToPurchasePlanningList, removeItem: removeFromPurchasePlanningList, items: purchasePlanningListItems } = usePurchasePlanningList();

  // Memoized data for performance
  const tableData = useMemo(() => data?.items || [], [data?.items]);
  const summary = useMemo(() => data?.summary, [data?.summary]);

  // Pagination calculations
  const totalCount = data?.totalCount || 0;
  const totalPages = Math.ceil(totalCount / filters.pageSize!);

  // Handler for filter changes
  const handleFilterChange = (
    newFilters: Partial<GetPurchaseStockAnalysisRequest>,
  ) => {
    setFilters((prev) => ({ ...prev, ...newFilters, pageNumber: 1 }));
  };

  // Handler for pagination
  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setFilters((prev) => ({ ...prev, pageNumber: newPage }));
    }
  };

  // Handler for page size change
  const handlePageSizeChange = (newPageSize: number) => {
    setFilters((prev) => ({ ...prev, pageSize: newPageSize, pageNumber: 1 }));
  };

  // Handler for sorting
  const handleSort = (column: StockAnalysisSortBy) => {
    setFilters((prev) => ({
      ...prev,
      sortBy: column,
      sortDescending: prev.sortBy === column ? !prev.sortDescending : true,
      pageNumber: 1,
    }));
  };

  // Export functionality
  const handleExport = useCallback(async () => {
    setIsExporting(true);
    try {
      const apiClient = await getAuthenticatedApiClient();
      const result = await apiClient.purchaseStockAnalysis_GetStockAnalysis(
        filters.fromDate ?? null,
        filters.toDate ?? null,
        filters.stockStatus,
        filters.materialCategory,
        filters.onlyConfigured,
        filters.searchTerm ?? null,
        undefined, // pageNumber — skipped on export
        undefined, // pageSize — skipped on export
        filters.sortBy,
        filters.sortDescending,
        true, // isExport
      );

      const today = new Date().toISOString().split("T")[0];
      await exportToXlsx(
        result.items ?? [],
        [
          { header: "Kód produktu", value: (row) => row.productCode },
          { header: "Název produktu", value: (row) => row.productName },
          { header: "Doplněk názvu", value: (row) => row.productNameSuffix },
          { header: "Typ produktu", value: (row) => row.productType },
          { header: "Dostupný sklad", value: (row) => row.availableStock },
          { header: "Objednané", value: (row) => row.orderedStock },
          { header: "Efektivní sklad", value: (row) => row.effectiveStock },
          { header: "Min. úroveň skladu", value: (row) => row.minStockLevel },
          { header: "Optimální sklad", value: (row) => row.optimalStockLevel },
          { header: "Spotřeba v období", value: (row) => row.consumptionInPeriod },
          { header: "Denní spotřeba", value: (row) => row.dailyConsumption },
          { header: "Dní do vyčerpání", value: (row) => row.daysUntilStockout },
          { header: "Efektivita skladu (%)", value: (row) => row.stockEfficiencyPercentage },
          { header: "Závažnost", value: (row) => row.severity },
          { header: "Min. objednací množství", value: (row) => row.minimalOrderQuantity },
          { header: "Poslední nákup – datum", value: (row) => row.lastPurchase?.date },
          { header: "Poslední nákup – dodavatel", value: (row) => row.lastPurchase?.supplierName },
          { header: "Poslední nákup – množství", value: (row) => row.lastPurchase?.amount },
          { header: "Poslední nákup – jedn. cena", value: (row) => row.lastPurchase?.unitPrice },
          { header: "Poslední nákup – celk. cena", value: (row) => row.lastPurchase?.totalPrice },
          { header: "Dodavatel", value: (row) => row.supplier },
          { header: "Doporučené objednací množství", value: (row) => row.recommendedOrderQuantity },
          { header: "Nakonfigurováno", value: (row) => row.isConfigured },
        ],
        `purchase-stock-analysis-${today}.xlsx`,
      );
    } catch {
      showError("Export selhal", "Nepodařilo se stáhnout data pro export.");
    } finally {
      setIsExporting(false);
    }
  }, [filters, showError]);

  // Quick date range selectors
  const QUICK_RANGE_PERIODS: Record<string, TimePeriod> = {
    last12months: TimePeriod.Y2Y,
    previousQuarter: TimePeriod.PreviousQuarter,
    nextQuarter: TimePeriod.FutureQuarter,
  };

  const handleQuickDateRange = (
    type: "last12months" | "previousQuarter" | "nextQuarter",
  ) => {
    const period = QUICK_RANGE_PERIODS[type];
    if (!period) return;
    const result = resolveTimePeriod(period);
    if (!result.primary) return;
    handleFilterChange({ fromDate: result.primary.from, toDate: result.primary.to });
  };

  // Get tooltip text for date range buttons
  const getDateRangeTooltip = (
    type: "last12months" | "previousQuarter" | "nextQuarter",
  ) => {
    const period = QUICK_RANGE_PERIODS[type];
    if (!period) return "";
    const result = resolveTimePeriod(period);
    if (!result.primary) return "";
    return `${result.primary.from.toLocaleDateString("cs-CZ")} - ${result.primary.to.toLocaleDateString("cs-CZ")}`;
  };

  // Purchase planning list functionality
  const isInPurchasePlanningList = (productCode?: string) => {
    if (!productCode) return false;
    return purchasePlanningListItems.some(item => item.productCode === productCode);
  };

  const handleTogglePurchasePlanningList = (item: any, event?: React.MouseEvent) => {
    if (event) {
      event.stopPropagation(); // Prevent row click
    }

    if (!item.productCode) return; // Exit if no product code

    if (isInPurchasePlanningList(item.productCode)) {
      removeFromPurchasePlanningList(item.productCode);
    } else {
      addToPurchasePlanningList({
        code: item.productCode,
        name: item.productName || "Neznámý produkt",
        supplier: item.supplier || "Neznámý dodavatel"
      });
    }
  };

  // Format stock display helper
  const formatStockDisplay = (available: number, ordered: number, effectiveStock: number) => {
    if (ordered > 0) {
      return {
        mainValue: formatNumber(effectiveStock, 0),
        subValue: `(${formatNumber(available, 0)}+${formatNumber(ordered, 0)})`,
        tooltip: `Skladem ${formatNumber(available, 0)}, objednáno ${formatNumber(ordered, 0)}`
      };
    }
    return {
      mainValue: formatNumber(available, 0),
      subValue: null,
      tooltip: `Skladem ${formatNumber(available, 0)}`
    };
  };

  // Get row background color based on severity (subtle coloring)
  const getRowColorClass = (severity: StockSeverity | undefined) => {
    switch (severity) {
      case StockSeverity.Critical:
        return "bg-red-50/30 hover:bg-red-50/50 dark:bg-red-900/20 dark:hover:bg-red-900/30";
      case StockSeverity.Low:
        return "bg-amber-50/30 hover:bg-amber-50/50 dark:bg-amber-900/20 dark:hover:bg-amber-900/30";
      case StockSeverity.Optimal:
        return "bg-emerald-50/30 hover:bg-emerald-50/50 dark:bg-emerald-900/20 dark:hover:bg-emerald-900/30";
      case StockSeverity.Overstocked:
        return "bg-blue-50/30 hover:bg-blue-50/50 dark:bg-blue-900/20 dark:hover:bg-blue-900/30";
      case StockSeverity.NotConfigured:
        return "bg-gray-50/30 hover:bg-gray-50/50 dark:bg-graphite-surface-2 dark:hover:bg-white/5";
      default:
        return "hover:bg-gray-50 dark:hover:bg-white/5";
    }
  };

  // Handle status filter click from summary cards
  const handleStatusFilterClick = (status: StockStatusFilter) => {
    handleFilterChange({ stockStatus: status });
  };

  // Modal handlers for product detail
  const handleRowClick = (item: any) => {
    setSelectedProductCode(item.productCode);
    setIsDetailModalOpen(true);
  };

  const handleCloseDetail = () => {
    setIsDetailModalOpen(false);
    setSelectedProductCode(null);
  };

  // Handle clicking on purchase planning panel item
  const handlePurchasePlanningItemClick = (item: { productCode: string; productName: string; supplier: string }) => {
    // Navigate to purchase order list page where user can create new orders
    // The purchase planning list will be available there for manual addition
    window.location.href = "/purchase/orders";
  };

  // Get color strip for product based on severity (only when not filtering by status)
  const getSeverityStripColor = (severity: StockSeverity | undefined) => {
    // Don't show strip when filtering by specific status
    if (filters.stockStatus !== StockStatusFilter.All) {
      return "";
    }

    switch (severity) {
      case StockSeverity.Critical:
        return "bg-red-500";
      case StockSeverity.Low:
        return "bg-amber-500";
      case StockSeverity.Optimal:
        return "bg-emerald-500";
      case StockSeverity.Overstocked:
        return "bg-blue-500";
      case StockSeverity.NotConfigured:
        return "bg-gray-400";
      default:
        return "";
    }
  };

  // Column definitions for grid layout
  const purchaseStockColumns = useMemo((): GridColumn<StockAnalysisItemDto>[] => [
    {
      id: 'product',
      header: 'Produkt',
      align: 'left',
      minWidth: 160,
      canHide: false,
      canReorder: false,
      renderCell: (item) => (
        <div className="flex items-center">
          {getSeverityStripColor(item.severity) && (
            <div className={`w-1 h-8 mr-3 rounded-sm ${getSeverityStripColor(item.severity)}`} />
          )}
          <div className="flex-1 min-w-0">
            <div className="text-sm text-gray-900 dark:text-graphite-text truncate">{item.productName}</div>
            <ProductNameSuffix suffix={item.productNameSuffix} />
            <div className="text-xs text-gray-500 dark:text-graphite-muted">{item.productCode}</div>
          </div>
        </div>
      ),
    },
    {
      id: 'planningList',
      header: '',
      align: 'center',
      defaultWidth: 60,
      canHide: false,
      canReorder: false,
      renderCell: (item) => (
        <button
          onClick={(e) => handleTogglePurchasePlanningList(item, e)}
          className={`p-1 rounded transition-colors ${
            isInPurchasePlanningList(item.productCode)
              ? 'text-green-600 dark:text-emerald-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30'
              : 'text-gray-400 dark:text-graphite-faint hover:text-indigo-600 dark:hover:text-graphite-accent hover:bg-indigo-50 dark:hover:bg-graphite-accent/10'
          }`}
          title={
            isInPurchasePlanningList(item.productCode)
              ? 'Odebrat ze seznamu k objednání'
              : 'Přidat do seznamu k objednání'
          }
        >
          {isInPurchasePlanningList(item.productCode) ? (
            <Check className="h-4 w-4" />
          ) : (
            <Plus className="h-4 w-4" />
          )}
        </button>
      ),
    },
    {
      id: 'availableStock',
      header: 'Skladem',
      align: 'right',
      canHide: true,
      canReorder: true,
      sortBy: StockAnalysisSortBy.AvailableStock,
      renderCell: (item) => {
        const display = formatStockDisplay(
          item.availableStock ?? 0,
          item.orderedStock ?? 0,
          item.effectiveStock ?? 0,
        );
        return (
          <div title={display.tooltip}>
            <div className="font-bold">{display.mainValue}</div>
            {display.subValue && (
              <div className="text-xs text-gray-500 dark:text-graphite-muted">{display.subValue}</div>
            )}
          </div>
        );
      },
    },
    {
      id: 'minOpt',
      header: 'Min/Opt',
      align: 'right',
      canHide: true,
      canReorder: true,
      renderCell: (item) => (
        <>
          <div>{formatNumber(item.minStockLevel, 0)}</div>
          <div className="text-xs text-gray-400 dark:text-graphite-faint">{formatNumber(item.optimalStockLevel, 0)}</div>
        </>
      ),
    },
    {
      id: 'consumption',
      header: 'Spotřeba',
      align: 'right',
      canHide: true,
      canReorder: true,
      sortBy: StockAnalysisSortBy.Consumption,
      renderCell: (item) => (
        <>
          <div>{formatNumber(item.consumptionInPeriod, 0)}</div>
          <div className="text-xs text-gray-500 dark:text-graphite-muted">{formatNumber(item.dailyConsumption, 2)}/den</div>
        </>
      ),
    },
    {
      id: 'stockEfficiency',
      header: 'NS',
      align: 'right',
      canHide: true,
      canReorder: true,
      sortBy: StockAnalysisSortBy.StockEfficiency,
      renderCell: (item) => (
        <div className="font-bold">
          {formatNumber(item.stockEfficiencyPercentage, 1)}%
        </div>
      ),
    },
    {
      id: 'moq',
      header: 'MOQ',
      align: 'right',
      canHide: true,
      canReorder: true,
      renderCell: (item) => <>{item.minimalOrderQuantity || '—'}</>,
    },
    {
      id: 'daysUntilStockout',
      header: 'Dny',
      align: 'right',
      canHide: true,
      canReorder: true,
      renderCell: (item) => (
        <>{item.daysUntilStockout ? formatNumber(item.daysUntilStockout, 0) : '∞'}</>
      ),
    },
    {
      id: 'supplier',
      header: 'Dodavatel',
      align: 'left',
      canHide: true,
      canReorder: true,
      renderCell: (item) => <>{item.supplier || '—'}</>,
    },
    {
      id: 'lastPurchase',
      header: 'Poslední nákup',
      align: 'left',
      defaultWidth: 224,
      canHide: true,
      canReorder: true,
      sortBy: StockAnalysisSortBy.LastPurchaseDate,
      renderCell: (item) =>
        item.lastPurchase ? (
          <div>
            <div className="font-medium">
              {item.lastPurchase.date
                ? new Date(item.lastPurchase.date).toLocaleDateString('cs-CZ')
                : '—'}
            </div>
            <div className="text-xs truncate max-w-20">
              {item.lastPurchase.supplierName || '—'}
            </div>
            <div className="text-xs font-medium">
              {formatNumber(item.lastPurchase.amount, 0)}ks @{' '}
              {formatCurrency(item.lastPurchase.unitPrice)}
            </div>
          </div>
        ) : (
          <span className="text-gray-400 dark:text-graphite-faint">—</span>
        ),
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [filters.stockStatus, purchasePlanningListItems]);

  const { orderedColumns, columnState, setColumnOrder, setColumnWidthLive, commitColumnWidth, toggleColumnVisibility, resetLayout } =
    useGridLayout('purchase-stock-analysis', purchaseStockColumns);

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-graphite-surface-2 px-4 py-8">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/40 rounded-lg p-6">
            <div className="flex items-center">
              <AlertTriangle className="h-5 w-5 text-red-400 mr-2" />
              <h3 className="text-lg font-medium text-red-800 dark:text-red-300">
                Chyba při načítání dat
              </h3>
            </div>
            <p className="mt-2 text-sm text-red-700 dark:text-red-300">
              {error instanceof Error ? error.message : "Neočekávaná chyba"}
            </p>
            <button
              onClick={() => refetch()}
              className="mt-4 bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/40 text-red-800 dark:text-red-300 px-4 py-2 rounded-md text-sm font-medium"
            >
              Zkusit znovu
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex flex-col w-full"
      style={{ height: PAGE_CONTAINER_HEIGHT }}
    >
      {/* Header - Fixed */}
      <div className="flex-shrink-0 mb-3">
        <h1 className="text-lg font-semibold text-gray-900 dark:text-graphite-text">
          Analýza skladových zásob
        </h1>
      </div>

      {/* Controls - Single Collapsible Block */}
      <div className="flex-shrink-0 bg-white dark:bg-graphite-surface rounded-lg shadow dark:shadow-soft-dark mb-4">
        <div className="p-3 border-b border-gray-200 dark:border-graphite-border">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setIsControlsCollapsed(!isControlsCollapsed)}
              className="flex items-center space-x-2 text-sm font-medium text-gray-900 dark:text-graphite-text hover:text-gray-700 dark:hover:text-graphite-muted"
            >
              {isControlsCollapsed ? (
                <ChevronRight className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
              <span>Filtry a nastavení</span>
              {summary && (
                <span className="text-xs text-gray-500 dark:text-graphite-muted">
                  ({summary.totalProducts} produktů)
                </span>
              )}
            </button>

            <div className="flex items-center space-x-3">
              {/* Always visible controls when collapsed */}
              {isControlsCollapsed && (
                <>
                  {/* Quick summary when collapsed - clickable */}
                  {summary && (
                    <div className="flex items-center space-x-2 text-xs">
                      <button
                        onClick={() =>
                          handleStatusFilterClick(StockStatusFilter.All)
                        }
                        className={`px-1 py-0.5 rounded transition-colors hover:bg-gray-100 dark:hover:bg-white/5 ${
                          filters.stockStatus === StockStatusFilter.All
                            ? "bg-gray-100 dark:bg-graphite-surface-2 ring-1 ring-gray-300 dark:ring-graphite-border"
                            : ""
                        }`}
                        title="Všechny produkty"
                      >
                        <span className="text-gray-700 dark:text-graphite-muted font-medium">
                          {summary.totalProducts}
                        </span>
                      </button>
                      <span className="text-gray-400 dark:text-graphite-faint">|</span>
                      <button
                        onClick={() =>
                          handleStatusFilterClick(StockStatusFilter.Critical)
                        }
                        className={`px-1 py-0.5 rounded transition-colors hover:bg-red-50 dark:hover:bg-red-900/30 ${
                          filters.stockStatus === StockStatusFilter.Critical
                            ? "bg-red-50 dark:bg-red-900/30 ring-1 ring-red-300 dark:ring-red-900/40"
                            : ""
                        }`}
                        title="Kritické zásoby"
                      >
                        <span className="text-red-600 dark:text-red-400 font-medium">
                          {summary.criticalCount}
                        </span>
                      </button>
                      <button
                        onClick={() =>
                          handleStatusFilterClick(StockStatusFilter.Low)
                        }
                        className={`px-1 py-0.5 rounded transition-colors hover:bg-amber-50 dark:hover:bg-amber-900/30 ${
                          filters.stockStatus === StockStatusFilter.Low
                            ? "bg-amber-50 dark:bg-amber-900/30 ring-1 ring-amber-300 dark:ring-amber-900/40"
                            : ""
                        }`}
                        title="Nízké zásoby"
                      >
                        <span className="text-orange-600 dark:text-orange-300 font-medium">
                          {summary.lowStockCount}
                        </span>
                      </button>
                      <button
                        onClick={() =>
                          handleStatusFilterClick(StockStatusFilter.Optimal)
                        }
                        className={`px-1 py-0.5 rounded transition-colors hover:bg-emerald-50 dark:hover:bg-emerald-900/30 ${
                          filters.stockStatus === StockStatusFilter.Optimal
                            ? "bg-emerald-50 dark:bg-emerald-900/30 ring-1 ring-emerald-300 dark:ring-emerald-900/40"
                            : ""
                        }`}
                        title="Optimální zásoby"
                      >
                        <span className="text-green-600 dark:text-emerald-400 font-medium">
                          {summary.optimalCount}
                        </span>
                      </button>
                    </div>
                  )}
                  {/* Search field when collapsed */}
                  <div className="flex-1 max-w-xs">
                    <div className="relative">
                      <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3 w-3 text-gray-400 dark:text-graphite-faint" />
                      <input
                        type="text"
                        value={filters.searchTerm || ""}
                        onChange={(e) =>
                          handleFilterChange({ searchTerm: e.target.value })
                        }
                        placeholder="Vyhledat..."
                        className="pl-7 w-full border border-gray-300 rounded-md px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-transparent dark:bg-graphite-surface-2 dark:border-graphite-border dark:text-graphite-text dark:placeholder-graphite-faint"
                      />
                    </div>
                  </div>
                </>
              )}

              {/* Material category - always visible */}
              <div
                role="group"
                aria-label="Typ materiálu"
                className="inline-flex rounded-md border border-gray-300 dark:border-graphite-border overflow-hidden"
              >
                {MATERIAL_CATEGORY_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    onClick={() =>
                      handleFilterChange({ materialCategory: option.value })
                    }
                    aria-pressed={filters.materialCategory === option.value}
                    className={`px-2.5 py-1 text-xs transition-colors border-r last:border-r-0 border-gray-300 dark:border-graphite-border ${
                      filters.materialCategory === option.value
                        ? "bg-indigo-600 text-white"
                        : "bg-white dark:bg-graphite-surface-2 text-gray-700 dark:text-graphite-muted hover:bg-gray-100 dark:hover:bg-graphite-hover"
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>

              {/* Action buttons - always visible */}
              <button
                onClick={() => refetch()}
                disabled={isRefetching}
                className="flex items-center px-2 py-1 border border-gray-300 dark:border-graphite-border rounded-md shadow-sm dark:shadow-soft-dark text-xs font-medium text-gray-700 dark:text-graphite-muted bg-white dark:bg-graphite-surface hover:bg-gray-50 dark:hover:bg-white/5 disabled:opacity-50"
              >
                <RefreshCw
                  className={`h-3 w-3 mr-1 ${isRefetching ? "animate-spin" : ""}`}
                />
                {isControlsCollapsed ? "" : "Obnovit"}
              </button>
              <button
                onClick={handleExport}
                disabled={isExporting}
                className="flex items-center px-2 py-1 border border-gray-300 dark:border-graphite-border rounded-md shadow-sm dark:shadow-soft-dark text-xs font-medium text-gray-700 dark:text-graphite-muted bg-white dark:bg-graphite-surface hover:bg-gray-50 dark:hover:bg-white/5 disabled:opacity-50"
              >
                {isExporting ? (
                  <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                ) : (
                  <Download className="h-3 w-3 mr-1" />
                )}
                {isControlsCollapsed ? "" : "Export"}
              </button>

              <ColumnChooser
                columns={purchaseStockColumns}
                columnState={columnState}
                onToggle={toggleColumnVisibility}
                onReset={resetLayout}
              />

              {/* Help */}
              <div className="relative group">
                <HelpCircle className="h-4 w-4 text-gray-400 dark:text-graphite-faint cursor-help" />
                <div className="absolute right-0 top-6 w-80 bg-gray-900 text-white text-xs rounded-lg p-3 opacity-0 group-hover:opacity-100 transition-opacity z-10 pointer-events-none">
                  <div className="space-y-2">
                    <div className="flex items-start">
                      <div className="w-3 h-3 bg-red-200 rounded-sm mr-2 mt-0.5 flex-shrink-0"></div>
                      <div>
                        <span className="font-medium text-red-200">
                          Kritické:
                        </span>{" "}
                        Zásoby pod minimální hranicí NEBO pod 20% optimálních
                        zásob
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="w-3 h-3 bg-amber-200 rounded-sm mr-2 mt-0.5 flex-shrink-0"></div>
                      <div>
                        <span className="font-medium text-amber-200">
                          Nízké:
                        </span>{" "}
                        Zásoby mezi 20-70% optimálních zásob
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="w-3 h-3 bg-emerald-200 rounded-sm mr-2 mt-0.5 flex-shrink-0"></div>
                      <div>
                        <span className="font-medium text-emerald-200">
                          Optimální:
                        </span>{" "}
                        Zásoby mezi 70-150% optimálních zásob
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="w-3 h-3 bg-blue-200 rounded-sm mr-2 mt-0.5 flex-shrink-0"></div>
                      <div>
                        <span className="font-medium text-blue-200">
                          Přeskladněno:
                        </span>{" "}
                        Zásoby nad 150% optimálních zásob
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="w-3 h-3 bg-gray-200 rounded-sm mr-2 mt-0.5 flex-shrink-0"></div>
                      <div>
                        <span className="font-medium text-gray-200">
                          Nezkonfigurováno:
                        </span>{" "}
                        Chybí nastavení minimálních a optimálních zásob
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {!isControlsCollapsed && (
          <div className="p-3 space-y-4">
            {/* Summary Cards */}
            {summary && (
              <div>
                <h3 className="text-xs font-medium text-gray-700 dark:text-graphite-muted mb-2">
                  Přehled stavů zásob
                </h3>
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <button
                    onClick={() =>
                      handleStatusFilterClick(StockStatusFilter.All)
                    }
                    className={`flex items-center px-2 py-1 rounded-md transition-colors hover:bg-gray-100 dark:hover:bg-white/5 ${
                      filters.stockStatus === StockStatusFilter.All
                        ? "bg-gray-100 dark:bg-graphite-surface-2 ring-1 ring-gray-300 dark:ring-graphite-border"
                        : ""
                    }`}
                  >
                    <Package className="h-3 w-3 text-blue-500 mr-1" />
                    <span className="text-gray-600 dark:text-graphite-muted">Celkem:</span>
                    <span className="font-semibold text-gray-900 dark:text-graphite-text ml-1">
                      {summary.totalProducts}
                    </span>
                  </button>

                  <button
                    onClick={() =>
                      handleStatusFilterClick(StockStatusFilter.Critical)
                    }
                    className={`flex items-center px-2 py-1 rounded-md transition-colors hover:bg-red-50 dark:hover:bg-red-900/30 ${
                      filters.stockStatus === StockStatusFilter.Critical
                        ? "bg-red-50 dark:bg-red-900/30 ring-1 ring-red-300 dark:ring-red-900/40"
                        : ""
                    }`}
                  >
                    <AlertTriangle className="h-3 w-3 text-red-500 mr-1" />
                    <span className="text-gray-600 dark:text-graphite-muted">Kritické:</span>
                    <span className="font-semibold text-red-600 dark:text-red-400 ml-1">
                      {summary.criticalCount}
                    </span>
                  </button>

                  <button
                    onClick={() =>
                      handleStatusFilterClick(StockStatusFilter.Low)
                    }
                    className={`flex items-center px-2 py-1 rounded-md transition-colors hover:bg-amber-50 dark:hover:bg-amber-900/30 ${
                      filters.stockStatus === StockStatusFilter.Low
                        ? "bg-amber-50 dark:bg-amber-900/30 ring-1 ring-amber-300 dark:ring-amber-900/40"
                        : ""
                    }`}
                  >
                    <TrendingDown className="h-3 w-3 text-orange-500 mr-1" />
                    <span className="text-gray-600 dark:text-graphite-muted">Nízké:</span>
                    <span className="font-semibold text-orange-600 dark:text-orange-300 ml-1">
                      {summary.lowStockCount}
                    </span>
                  </button>

                  <button
                    onClick={() =>
                      handleStatusFilterClick(StockStatusFilter.Optimal)
                    }
                    className={`flex items-center px-2 py-1 rounded-md transition-colors hover:bg-emerald-50 dark:hover:bg-emerald-900/30 ${
                      filters.stockStatus === StockStatusFilter.Optimal
                        ? "bg-emerald-50 dark:bg-emerald-900/30 ring-1 ring-emerald-300 dark:ring-emerald-900/40"
                        : ""
                    }`}
                  >
                    <CheckCircle className="h-3 w-3 text-green-500 mr-1" />
                    <span className="text-gray-600 dark:text-graphite-muted">Optimální:</span>
                    <span className="font-semibold text-green-600 dark:text-emerald-400 ml-1">
                      {summary.optimalCount}
                    </span>
                  </button>

                  <button
                    onClick={() =>
                      handleStatusFilterClick(StockStatusFilter.Overstocked)
                    }
                    className={`flex items-center px-2 py-1 rounded-md transition-colors hover:bg-blue-50 dark:hover:bg-blue-900/30 ${
                      filters.stockStatus === StockStatusFilter.Overstocked
                        ? "bg-blue-50 dark:bg-blue-900/30 ring-1 ring-blue-300 dark:ring-blue-900/40"
                        : ""
                    }`}
                  >
                    <Package className="h-3 w-3 text-blue-500 mr-1" />
                    <span className="text-gray-600 dark:text-graphite-muted">Přeskladněno:</span>
                    <span className="font-semibold text-blue-600 dark:text-blue-400 ml-1">
                      {summary.overstockedCount}
                    </span>
                  </button>

                  <button
                    onClick={() =>
                      handleStatusFilterClick(StockStatusFilter.NotConfigured)
                    }
                    className={`flex items-center px-2 py-1 rounded-md transition-colors hover:bg-gray-50 dark:hover:bg-white/5 ${
                      filters.stockStatus === StockStatusFilter.NotConfigured
                        ? "bg-gray-50 dark:bg-graphite-surface-2 ring-1 ring-gray-300 dark:ring-graphite-border"
                        : ""
                    }`}
                  >
                    <Settings className="h-3 w-3 text-gray-500 mr-1" />
                    <span className="text-gray-600 dark:text-graphite-muted">Nezkonfigurováno:</span>
                    <span className="font-semibold text-gray-600 dark:text-graphite-muted ml-1">
                      {summary.notConfiguredCount}
                    </span>
                  </button>
                </div>
              </div>
            )}

            {/* Filters */}
            <div>
              <h3 className="text-xs font-medium text-gray-700 dark:text-graphite-muted mb-2">Filtry</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                {/* Search */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-graphite-muted mb-1">
                    Vyhledat
                  </label>
                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3 w-3 text-gray-400 dark:text-graphite-faint" />
                    <input
                      type="text"
                      value={filters.searchTerm || ""}
                      onChange={(e) =>
                        handleFilterChange({ searchTerm: e.target.value })
                      }
                      placeholder="Kód, název, dodavatel..."
                      className="pl-8 w-full border border-gray-300 rounded-md px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-transparent dark:bg-graphite-surface-2 dark:border-graphite-border dark:text-graphite-text dark:placeholder-graphite-faint"
                    />
                  </div>
                </div>

                {/* Date From */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-graphite-muted mb-1">
                    Od data
                  </label>
                  <input
                    type="date"
                    value={filters.fromDate?.toISOString().split("T")[0] || ""}
                    onChange={(e) =>
                      handleFilterChange({ fromDate: new Date(e.target.value) })
                    }
                    className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-transparent dark:bg-graphite-surface-2 dark:border-graphite-border dark:text-graphite-text dark:placeholder-graphite-faint"
                  />
                </div>

                {/* Date To */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-graphite-muted mb-1">
                    Do data
                  </label>
                  <input
                    type="date"
                    value={filters.toDate?.toISOString().split("T")[0] || ""}
                    onChange={(e) =>
                      handleFilterChange({ toDate: new Date(e.target.value) })
                    }
                    className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-transparent dark:bg-graphite-surface-2 dark:border-graphite-border dark:text-graphite-text dark:placeholder-graphite-faint"
                  />
                </div>

                {/* Quick Date Range Selectors */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-graphite-muted mb-1">
                    Rychlé volby
                  </label>
                  <div className="space-y-1.5">
                    <div className="flex gap-1">
                      <button
                        onClick={() => handleQuickDateRange("last12months")}
                        className="px-1.5 py-0.5 text-xs bg-gray-100 dark:bg-graphite-surface-2 hover:bg-gray-200 dark:hover:bg-graphite-hover text-gray-700 dark:text-graphite-muted rounded border border-gray-300 dark:border-graphite-border transition-colors whitespace-nowrap"
                        title={getDateRangeTooltip("last12months")}
                      >
                        Y2Y
                      </button>
                      <button
                        onClick={() => handleQuickDateRange("previousQuarter")}
                        className="px-1.5 py-0.5 text-xs bg-gray-100 dark:bg-graphite-surface-2 hover:bg-gray-200 dark:hover:bg-graphite-hover text-gray-700 dark:text-graphite-muted rounded border border-gray-300 dark:border-graphite-border transition-colors whitespace-nowrap"
                        title={getDateRangeTooltip("previousQuarter")}
                      >
                        PrevQ
                      </button>
                      <button
                        onClick={() => handleQuickDateRange("nextQuarter")}
                        className="px-1.5 py-0.5 text-xs bg-gray-100 dark:bg-graphite-surface-2 hover:bg-gray-200 dark:hover:bg-graphite-hover text-gray-700 dark:text-graphite-muted rounded border border-gray-300 dark:border-graphite-border transition-colors whitespace-nowrap"
                        title={getDateRangeTooltip("nextQuarter")}
                      >
                        NextQ
                      </button>
                    </div>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={filters.onlyConfigured || false}
                        onChange={(e) =>
                          handleFilterChange({
                            onlyConfigured: e.target.checked,
                          })
                        }
                        className="rounded border-gray-300 dark:border-graphite-border text-indigo-600 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                      />
                      <span className="ml-1.5 text-xs text-gray-700 dark:text-graphite-muted">
                        Pouze konfigurované
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Results Table */}
      <div className="flex-1 bg-white dark:bg-graphite-surface rounded-lg shadow dark:shadow-soft-dark overflow-hidden flex flex-col min-h-0">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="h-8 w-8 animate-spin text-gray-400 dark:text-graphite-faint" />
            <span className="ml-2 text-gray-600 dark:text-graphite-muted">Načítání dat...</span>
          </div>
        ) : tableData.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <Package className="h-12 w-12 text-gray-400 dark:text-graphite-faint mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-graphite-text mb-2">
                Žádné výsledky
              </h3>
              <p className="text-gray-600 dark:text-graphite-muted">
                Zkuste upravit filtry nebo vyhledávací kritéria.
              </p>
            </div>
          </div>
        ) : (
          <div className="flex-1 overflow-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-graphite-border">
              <GridHeader
                columns={orderedColumns}
                columnState={columnState}
                activeSortKey={filters.sortBy as string | undefined}
                sortDescending={filters.sortDescending}
                onSort={(key) => handleSort(key as StockAnalysisSortBy)}
                onReorder={setColumnOrder}
                onResizeChange={setColumnWidthLive}
                onResizeEnd={commitColumnWidth}
              />
              <tbody className="bg-white dark:bg-graphite-surface divide-y divide-gray-200 dark:divide-graphite-border">
                {tableData.map((item) => (
                  <tr
                    key={item.productCode}
                    className={`${getRowColorClass(item.severity)} hover:bg-gray-50 dark:hover:bg-white/5 cursor-pointer transition-colors duration-150`}
                    onClick={() => handleRowClick(item)}
                    title="Klikněte pro zobrazení detailu produktu"
                  >
                    {orderedColumns.map((col) => {
                      const state = columnState.find((s) => s.id === col.id);
                      return (
                        <td
                          key={col.id}
                          className={`px-6 py-4 whitespace-nowrap text-xs text-gray-900 dark:text-graphite-text ${col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : ''} ${col.cellClassName || ''}`}
                          style={state?.width ? { width: state.width, minWidth: col.minWidth } : col.minWidth ? { minWidth: col.minWidth } : undefined}
                        >
                          {col.renderCell(item)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination - Compact */}
        {totalCount > 0 && (
          <div className="flex-shrink-0 bg-white dark:bg-graphite-surface px-3 py-2 flex items-center justify-between border-t border-gray-200 dark:border-graphite-border text-xs">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => handlePageChange(filters.pageNumber! - 1)}
                disabled={filters.pageNumber! <= 1}
                className="relative inline-flex items-center px-2 py-1 border border-gray-300 dark:border-graphite-border text-xs font-medium rounded text-gray-700 dark:text-graphite-muted bg-white dark:bg-graphite-surface hover:bg-gray-50 dark:hover:bg-white/5 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Předchozí
              </button>
              <button
                onClick={() => handlePageChange(filters.pageNumber! + 1)}
                disabled={filters.pageNumber! >= totalPages}
                className="ml-2 relative inline-flex items-center px-2 py-1 border border-gray-300 dark:border-graphite-border text-xs font-medium rounded text-gray-700 dark:text-graphite-muted bg-white dark:bg-graphite-surface hover:bg-gray-50 dark:hover:bg-white/5 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Další
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div className="flex items-center space-x-3">
                <p className="text-xs text-gray-600 dark:text-graphite-muted">
                  {(filters.pageNumber! - 1) * filters.pageSize! + 1}-
                  {Math.min(
                    filters.pageNumber! * filters.pageSize!,
                    totalCount,
                  )}{" "}
                  z {totalCount}
                </p>
                <div className="flex items-center space-x-1">
                  <span className="text-xs text-gray-600 dark:text-graphite-muted">Zobrazit:</span>
                  <select
                    value={filters.pageSize}
                    onChange={(e) =>
                      handlePageSizeChange(Number(e.target.value))
                    }
                    className="border border-gray-300 rounded px-1 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-transparent dark:bg-graphite-surface-2 dark:border-graphite-border dark:text-graphite-text"
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
              </div>
              <div>
                <nav
                  className="relative z-0 inline-flex rounded shadow-sm -space-x-px"
                  aria-label="Pagination"
                >
                  <button
                    onClick={() => handlePageChange(filters.pageNumber! - 1)}
                    disabled={filters.pageNumber! <= 1}
                    className="relative inline-flex items-center px-1 py-1 rounded-l border border-gray-300 dark:border-graphite-border bg-white dark:bg-graphite-surface text-xs font-medium text-gray-500 dark:text-graphite-muted hover:bg-gray-50 dark:hover:bg-white/5 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="h-3 w-3" />
                  </button>

                  {/* Page numbers */}
                  {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                    let pageNum: number;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (filters.pageNumber! <= 3) {
                      pageNum = i + 1;
                    } else if (filters.pageNumber! >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = filters.pageNumber! - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`relative inline-flex items-center px-2 py-1 border text-xs font-medium ${
                          pageNum === filters.pageNumber!
                            ? "z-10 bg-indigo-50 dark:bg-graphite-accent/10 border-indigo-500 dark:border-graphite-accent text-indigo-600 dark:text-graphite-accent"
                            : "bg-white dark:bg-graphite-surface border-gray-300 dark:border-graphite-border text-gray-500 dark:text-graphite-muted hover:bg-gray-50 dark:hover:bg-white/5"
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}

                  <button
                    onClick={() => handlePageChange(filters.pageNumber! + 1)}
                    disabled={filters.pageNumber! >= totalPages}
                    className="relative inline-flex items-center px-1 py-1 rounded-r border border-gray-300 dark:border-graphite-border bg-white dark:bg-graphite-surface text-xs font-medium text-gray-500 dark:text-graphite-muted hover:bg-gray-50 dark:hover:bg-white/5 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="h-3 w-3" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Product Detail Modal */}
      <CatalogDetail
        productCode={selectedProductCode}
        isOpen={isDetailModalOpen}
        onClose={handleCloseDetail}
        defaultTab="history"
      />

      {/* Purchase Planning List Panel */}
      <PurchasePlanningListPanel
        isVisible={false}
        onItemClick={handlePurchasePlanningItemClick}
      />
    </div>
  );
};

export default PurchaseStockAnalysis;
